import { Injectable, OnDestroy } from '@angular/core'
import { AgGridApi } from '@northfork/ag-grid'
import { RowNode } from '@ag-grid-community/core/dist/cjs/entities/rowNode'
import {
  EventType,
  HeliosMessageType,
  SessionBasedEvent,
  SessionMessagingService,
} from '@helios/messaging'
import { map, mergeAll, takeUntil, tap } from 'rxjs/operators'
import { Subject } from 'rxjs'

/**
 * @internal
 */
export const UPDATE_TIMEOUT = 5000
const UPDATE_TIMOUT_INTERVAL = 1000

@Injectable({ providedIn: 'root' })
export class MarginEffectSpinnerService implements OnDestroy {
  private readonly updatingRows: Map<string, number> = new Map()
  private readonly updateTimeoutChecker = setInterval(() => {
    if (this.updatingRows.size === 0) {
      return
    }

    const timeOutTime = new Date()
    timeOutTime.setMilliseconds(timeOutTime.getMilliseconds() - UPDATE_TIMEOUT)

    for (const [rowId, time] of this.updatingRows) {
      if (time < timeOutTime.valueOf()) {
        this.removeUpdatingRow(rowId)
      }
    }
  }, UPDATE_TIMOUT_INTERVAL)
  private readonly destroySubject$ = new Subject()
  private agGridApi?: AgGridApi

  constructor(private readonly messagingService: SessionMessagingService) {
    this.messagingService
      .on<{ rowIds: string[] }, SessionBasedEvent<{ rowIds: string[] }>>(
        EventType.MESSAGE,
        HeliosMessageType.CWTransactionUpdate,
      )
      .pipe(
        takeUntil(this.destroySubject$),
        map(update => update.payload.rowIds),
        mergeAll(),
        tap(rowId => this.removeUpdatingRow(rowId)),
      )
      .subscribe()
  }

  ngOnDestroy() {
    clearInterval(this.updateTimeoutChecker)
    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  registerGridApi(agGridApi: AgGridApi) {
    this.updatingRows.clear()
    this.agGridApi = agGridApi
  }

  addUpdatingRow(rowId: string): void {
    this.updatingRows.set(rowId, Date.now())
    this.triggerRedraw([rowId])
  }

  removeUpdatingRow(rowId: string): void {
    if (this.updatingRows.delete(rowId)) {
      this.triggerRedraw([rowId])
    }
  }

  isUpdating(rowId: string): boolean {
    return this.updatingRows.has(rowId)
  }

  private triggerRedraw(rowIds: string[]): void {
    if (this.agGridApi == null) {
      return
    }

    const rowNodes: RowNode[] = []
    for (const rowId of rowIds) {
      const rowNode = this.agGridApi.api.getRowNode(rowId)
      if (rowNode != null) {
        rowNodes.push(rowNode)
      }
    }

    if (rowNodes.length > 0) {
      this.agGridApi.api.redrawRows({ rowNodes })
    }
  }
}
